import React, { useState } from 'react'
import FormSectionTitle from '../../components/FormSectionTitle'
import Typography from '../../components/Typography'
import { makeStyles } from '@material-ui/core'
import IconButton from '../../components/IconButton'
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle'
import type { Moment } from 'moment'
import moment from 'moment'
import { DateOverridePicker } from '../../components/DatePicker/DateOverridePicker'
import { useFormikContext } from 'formik'
import type { ProfileFormikValues } from '../ProfileFormik'
import type { DateRange } from '@paintscout/api'
import classNames from 'classnames'

const useStyles = makeStyles((theme) => ({
  content: {},
  row: {
    display: 'grid',
    gridTemplateColumns: '6fr 1fr',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('lg')]: {
      gridTemplateColumns: '10fr 1fr'
    }
  },
  dateRanges: {
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    padding: theme.spacing(1, 2),
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
    '&:hover': {
      cursor: 'pointer',
      borderColor: 'transparent',
      boxShadow: `0 0 0 2px ${theme.paintscout.palette.brand.Neutral.P70}`,
      boxSizing: 'border-box'
    }
  },
  openDateRanges: {
    borderColor: 'transparent',
    boxShadow: `0 0 0 2px ${theme.paintscout.palette.primary} !important`,
    boxSizing: 'border-box'
  },
  removeButtonWrapper: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(2)
  },
  removeButton: {
    padding: theme.spacing(0),
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        color: theme.palette.error.main
      }
    }
  },
  year: {
    margin: theme.spacing(2, 0, 1, 0)
  },
  unavailable: {
    fontStyle: 'italic',
    fontWeight: 500,
    color: theme.palette.text.secondary
  }
}))

function formatDateRange(group: { start: number; end: number }) {
  const momentStart = moment(group.start)
  const momentEnd = moment(group.end)
  const isSameDay = momentStart.isSame(momentEnd, 'day')
  const isSameMonth = momentStart.isSame(momentEnd, 'month')
  const isSameYear = momentStart.isSame(momentEnd, 'year')

  if (isSameDay) {
    return momentStart.format('MMMM D')
  }
  if (isSameMonth && isSameYear) {
    return `${momentStart.format('MMMM D')} - ${momentEnd.format('D')}`
  }
  if (isSameYear) {
    return (
      <>
        {momentStart.format('MMMM D')} –<br />
        {momentEnd.format('MMMM D')}
      </>
    )
  }
  return `${momentStart.format('MMMM D')} - ${momentEnd.format('MMMM D')}`
}

function formatTimeRange(group: { startTime: number; endTime: number }) {
  const startTime = moment(group.startTime).format('h:mma')
  const endTime = moment(group.endTime).format('h:mma')
  if (startTime === endTime) {
    return 'Unavailable'
  }
  return `${startTime} - ${endTime}`
}

function groupOverrides(overrides: DateRange[]) {
  if (!overrides.length) return []

  // Collect all time windows for each day
  const byDay: { [date: string]: { start: number; end: number }[] } = {}
  overrides.forEach((o) => {
    const day = moment(o.start).format('YYYY-MM-DD')
    if (!byDay[day]) byDay[day] = []
    byDay[day].push({ start: o.start, end: o.end })
  })

  // For each day, sort and stringify its time windows for grouping
  const days = Object.keys(byDay).sort()
  const dayGroups: {
    date: string
    timeWindows: { start: number; end: number }[]
    timeKey: string
  }[] = days.map((date) => {
    const timeWindows = byDay[date].sort((a, b) => a.start - b.start)
    const timeKey = timeWindows
      .map((w) => `${moment(w.start).format('HH:mm')}-${moment(w.end).format('HH:mm')}`)
      .join('|')
    return { date, timeWindows, timeKey }
  })

  // Group consecutive days with the same timeKey
  const result: {
    start: number
    end: number
    days: string[]
    timeGroups: { startTime: number; endTime: number }[]
    multi: boolean
    date?: string
  }[] = []

  let groupStartIdx = 0
  while (groupStartIdx < dayGroups.length) {
    let groupEndIdx = groupStartIdx
    const { timeKey } = dayGroups[groupStartIdx]
    // Expand group while next day is consecutive and has same timeKey
    while (
      groupEndIdx + 1 < dayGroups.length &&
      dayGroups[groupEndIdx + 1].timeKey === timeKey &&
      moment(dayGroups[groupEndIdx + 1].date).diff(moment(dayGroups[groupEndIdx].date), 'days') === 1
    ) {
      groupEndIdx++
    }
    const groupDays = dayGroups.slice(groupStartIdx, groupEndIdx + 1)
    result.push({
      start: moment(groupDays[0].date).valueOf(),
      end: moment(groupDays[groupDays.length - 1].date).valueOf(),
      days: groupDays.map((g) => g.date),
      timeGroups: groupDays[0].timeWindows.map((w) => ({
        startTime: w.start,
        endTime: w.end
      })),
      multi: groupDays.length > 1,
      date: groupDays.length === 1 ? groupDays[0].date : undefined
    })
    groupStartIdx = groupEndIdx + 1
  }

  // Sort by start date ascending
  return result.sort((a, b) => a.start - b.start)
}

export default function DateOverrides() {
  const classes = useStyles()
  const { values, setFieldValue } = useFormikContext<ProfileFormikValues>()
  const { preferences } = values

  const [openPicker, setOpenPicker] = useState<{ year: string; index: number } | null>(null)

  const today = moment().startOf('day')
  const allOverrides = (preferences?.availability?.overrides || [])
    .filter((o) => moment(o.start).isSameOrAfter(today, 'day'))
    .sort((a, b) => a.start - b.start)
  const grouped = groupOverrides(allOverrides)

  // Group the grouped overrides by year
  const groupedByYear: { [year: string]: typeof grouped } = {}
  grouped.forEach((group) => {
    // Use the start date for the year
    const year = moment(group.start).format('YYYY')
    if (!groupedByYear[year]) groupedByYear[year] = []
    groupedByYear[year].push(group)
  })

  const sortedYears = Object.keys(groupedByYear).sort()

  const handleOverridesChange = (
    overrides: { start: Moment; end: Moment }[],
    groupToReplace?: { days: string[]; timeGroups: { startTime: number; endTime: number }[] }
  ) => {
    const existing = preferences?.availability?.overrides || []

    let filtered = existing
    if (groupToReplace) {
      // Remove all overrides that match the group's days and times
      filtered = existing.filter((o) => {
        const oDay = moment(o.start).format('YYYY-MM-DD')
        const matchesDay = groupToReplace.days.includes(oDay)
        const matchesTime = groupToReplace.timeGroups.some(
          (tg) =>
            moment(o.start).format('HH:mm') === moment(tg.startTime).format('HH:mm') &&
            moment(o.end).format('HH:mm') === moment(tg.endTime).format('HH:mm')
        )
        return !(matchesDay && matchesTime)
      })
    }

    const newOverrides = overrides.map((override) => ({
      start: override.start.unix() * 1000,
      end: override.end.unix() * 1000
    }))

    setFieldValue('preferences.availability.overrides', [...filtered, ...newOverrides])
  }

  return (
    <>
      <FormSectionTitle
        title={
          <Typography variant="h3" weight={500}>
            Date Overrides
          </Typography>
        }
        subTitle="Customize your default schedule for select days."
        rightContent={<DateOverridePicker onConfirm={handleOverridesChange} />}
      />
      <div className={classes.content}>
        {sortedYears.map((year) => (
          <React.Fragment key={year}>
            <Typography weight={600} variant="subtitle2" className={classes.year}>
              {year}
            </Typography>
            {groupedByYear[year].map((group, index) => (
              <div key={index} className={classes.row}>
                <DateOverridePicker
                  onConfirm={(overrides) => handleOverridesChange(overrides, group)}
                  initialDays={group.days}
                  initialTimes={group.timeGroups}
                  onPopperOpen={() => setOpenPicker({ year, index })}
                  onPopperClose={() => {
                    if (openPicker?.year !== year || openPicker?.index !== index) {
                      return
                    }
                    setOpenPicker(null)
                  }}
                  trigger={
                    <div
                      className={classNames(classes.dateRanges, {
                        [classes.openDateRanges]: openPicker?.year === year && openPicker?.index === index
                      })}
                    >
                      <div>
                        <Typography weight={600}>
                          {group.multi
                            ? formatDateRange(group)
                            : group.date
                              ? moment(group.date).format('MMMM D')
                              : formatDateRange(group)}
                        </Typography>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {group.timeGroups ? (
                          group.timeGroups.map((tg, i) => (
                            <Typography
                              className={formatTimeRange(tg) === 'Unavailable' ? classes.unavailable : null}
                              key={i}
                            >
                              {formatTimeRange(tg)}
                            </Typography>
                          ))
                        ) : (
                          <Typography>
                            {formatTimeRange({
                              startTime: group.start,
                              endTime: group.end
                            })}
                          </Typography>
                        )}
                      </div>
                    </div>
                  }
                />
                <div className={classes.removeButtonWrapper}>
                  <IconButton
                    className={classes.removeButton}
                    size="small"
                    onClick={() => {
                      setFieldValue(
                        'preferences.availability.overrides',
                        preferences?.availability?.overrides.filter((o) => {
                          if (group.multi) {
                            const oDay = moment(o.start).startOf('day')
                            const gStart = moment(group.start).startOf('day')
                            const gEnd = moment(group.end).startOf('day')
                            const inRange = oDay.isSameOrAfter(gStart) && oDay.isSameOrBefore(gEnd)
                            const sameTime =
                              moment(o.start).format('HH:mm') ===
                                moment(group.timeGroups[0].startTime).format('HH:mm') &&
                              moment(o.end).format('HH:mm') === moment(group.timeGroups[0].endTime).format('HH:mm')
                            return !(inRange && sameTime)
                          } else if (group.timeGroups && group.date) {
                            return moment(o.start).format('YYYY-MM-DD') !== group.date
                          } else {
                            return !(
                              moment(o.start).isSame(group.start, 'minute') && moment(o.end).isSame(group.end, 'minute')
                            )
                          }
                        })
                      )
                    }}
                  >
                    <RemoveCircleIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
    </>
  )
}
